/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Dialog } from '@mui/material';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { useMst } from '../../../models/rootModel';

import { useAnalytics } from '../../../utils/analytics';
import { sendGTMEvent } from '../../../utils/googleTagManager';
import { goBackToMobileApp } from '../../../utils/mobileApp';
import { ActionName } from '../../../utils/mobileApp/mobileApp.types';
import { getDocumentId, isMobileAppWebView } from '../../../utils/utilsFunctions';

import { CardForm } from './CardForm';
import { PaymentMethod, PaymentPageStepProps } from './paymentStep.types';
import { planDataExtractor } from './planDataExtractor';

import { SignUpData } from '../../../models/types/UserDiscoveryResponse.types';
import { calculateAmountWithDiscount, calculatePurchasePrice, formatAmount } from '../../../utils/price/price.utils';
import { LogoWithName } from '../../common/LogoWithName';
import { BaseStepV2 } from '../../common/v2/BaseStepV2';
import { StepV2MainContent } from '../../common/v2/StepV2MainContent';
import { ContentBoxWrapper, MainContentBox } from '../../common/v2/Box/Box.style';
import { MobilePaymentSummary } from '../../common/PaymentSummary/MobilePaymentSummary';
import { PaymentPageTwoSidedLayout, PaymentSecurityBoxWrapper, PaymentSummaryCol } from './PaymentPage.style';
import { PaymentSecurityBox } from './PaymentSecurityBox';
import { PaymentSummary } from '../../common/PaymentSummary/PaymentSummary';
import { markConversion } from '../../../utils/tapfiliate';
import { ContentCol } from '../../common/v2/TwoSidedLayout/TwoSidedLayout.style';

export const DEFAULT_MONTHLY_PLAN_KEY = '0';

/**
 * @deprecated
 */
export const PaymentPage = observer((props: PaymentPageStepProps) => {
  const { UserPayment, UserDiscoveryResponse, Step } = useMst();
  const { nextStep, isActive, showCouponInput } = props;

  const stripe = useStripe();
  const analytics = useAnalytics();

  const elements = useElements();
  const [isCardInfoCorrect, setIsCardInfoCorrect] = useState(false);
  const [alert, setAlert] = useState('');
  const paymentMethod = PaymentMethod.CreditCard;
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const plan = UserPayment.getChoosenPlan();
  const currentPlan = planDataExtractor(plan);

  const discount = toJS(UserPayment.discount);

  const analyticsObject = useRef({
    ...UserDiscoveryResponse.utms,
    stepId: props.id,
    stepType: props.type,
    stepIndex: props.stepIndex,
    stepTitle: props.title,
    origin: Step.origin,
    userID: UserDiscoveryResponse.accountId,
  });

  useEffect(() => {
    if (isActive) {
      analytics.track('view checkout flow');
      window.scrollTo(0, 0);
    }
  }, [isActive]);

  const confirmPaymentAction = () => {
    const discountAmount = discount?.coupon?.duration
      ? (currentPlan.sanitizedAmount / 100) * (discount.coupon?.percentOff / 100)
      : 0;

    const amount = calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount);

    const currency = 'USD';

    const promoCode = UserPayment.appliedCouponCode || 'NA';
    sendGTMEvent({
      event: 'purchase',
      user_id: UserDiscoveryResponse.accountId,
      phone: UserDiscoveryResponse.userPhoneNumber,
      full_name: UserDiscoveryResponse.userName,
      email: UserDiscoveryResponse.userEmail,
      ecommerce: {
        transaction_id: UserPayment.purchaseId,
        value: calculatePurchasePrice(amount),
        currency, // the currency code, e.g. “USD”
        tax: 0,
        coupon: promoCode, // Coupon code, if applied
        first_time_client: 'yes',
        transaction_method: paymentMethod,
        user_id: UserDiscoveryResponse.accountId,
        items: [
          {
            item_id: UserPayment.planId,
            item_name: 'Premium',
            item_brand: 'Circles',
            coupon: promoCode,
            currency,
            discount: discountAmount.toFixed(2),
            price: formatAmount(currentPlan.sanitizedAmount),
            quantity: 1,
          },
        ],
      },
    });

    sendGTMEvent({
      event: 'awin.dl.ready',
      transactionTotal: formatAmount(amount),
      transactionCurrency: currency,
      transactionID: UserPayment.purchaseId,
      transactionPromoCode: promoCode,
    });

    markConversion(UserDiscoveryResponse.accountId ?? 'unknown', {
      amountInCents: currentPlan.sanitizedAmount,
      currencyIsoCode: plan?.currency ?? 'usd',
    });

    if (props.totalSteps === props.stepIndex + 1 && isMobileAppWebView) {
      goBackToMobileApp({ action: ActionName.Close });
      return;
    }

    setIsLoading(false);
    nextStep();
  };

  const signUpUser = async () => {
    const dataToSend: SignUpData = UserDiscoveryResponse.signUpDateToSend;

    if (UserPayment.appliedCouponCode) {
      dataToSend.couponCode = UserPayment.appliedCouponCode;
    }

    try {
      const resSignUpUser = await UserDiscoveryResponse.signUpUser({ ...dataToSend, origin: Step.origin ?? '' });
      const { accountId } = resSignUpUser.data;

      sendGTMEvent({
        event: 'signup_completed',
        user_id: accountId,
        phone: dataToSend.phone,
        full_name: dataToSend.name,
        email: dataToSend.email,
      });

      analytics.track(`click signUp complete ${props.type}`, {
        stepId: props.id,
        stepType: props.type,
        stepIndex: props.stepIndex,
        stepTitle: props.title ?? '',
        ...UserDiscoveryResponse.utms,
        origin: Step.origin,
        userID: accountId,
      });
    } catch (_error) {}
  };

  const handleFormSubmit = async (ev?: PaymentRequestPaymentMethodEvent) => {
    const choosenPaymentMethod = ev ? ev.walletName : 'creditCard';
    analytics.track('pay confirmation click', {
      paymentType: choosenPaymentMethod,
      productSku: currentPlan.sanitizedId,
      price: calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount),
      planPrice: currentPlan.sanitizedAmount,
    });
    setIsCardInfoCorrect(false);
    setIsLoading(true);
    if (!stripe || !elements) return;
    if (UserPayment.createAccountOnPay && !UserDiscoveryResponse.accountId) {
      await signUpUser();
    }
    await UserPayment.getStripeSecretOnly();
    let result;
    if (ev) {
      result = await stripe.confirmCardSetup(UserPayment.clientSecret, {
        payment_method: ev.paymentMethod.id,
      });
      // this closes the browser’s payment interface
      ev.complete('success');
    } else {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;
      result = await stripe.confirmCardSetup(UserPayment.clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
    }

    if (result.error) {
      console.error('bad payment', result.error);
      analytics.track(`view payment error`, {
        ...analyticsObject,
        error: result.error,
        declineReason: result.error.code,
        productSku: currentPlan.sanitizedId,
        paymentType: choosenPaymentMethod,
      });

      setAlert(result.error.message || 'An error occurred');
      setIsLoading(false);
    } else {
      if (result.setupIntent.status === 'succeeded') {
        const { payment_method } = result.setupIntent;
        UserPayment.processPayment({
          paymentMethodId: payment_method as string,
          priceId: currentPlan.sanitizedId,
          discount: UserPayment.discount,
          meta: { choosenPaymentMethod },
          onboardingDocumentId: getDocumentId(),
        })
          .then((res) => {
            if (res.kind === 'ok') {
              confirmPaymentAction();
            } else {
              setIsLoading(false);
              throw new Error(res.data?._uiNotice?.title ?? 'Error, please try another card', { cause: res.data });
            }
          })
          .catch((error) => {
            console.log(error.cause);

            analytics.track(`view payment error`, {
              ...analyticsObject,
              paymentType: choosenPaymentMethod,
              productSku: currentPlan.sanitizedId,
              declineReason: error?.cause?.code ?? 'unknown',
              error,
            });
            setIsLoading(false);
            setAlert(error.message);
            UserPayment.getStripePlans();
            UserPayment.getStripeSecretOnly();
          });
      }
    }
  };

  useEffect(() => {
    if (stripe && currentPlan.sanitizedAmount !== 0) {
      const amount = calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount);
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: currentPlan.sanitizedPlanTitle,
          amount,
        },
      });
      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe]);

  useEffect(() => {
    if (paymentRequest) {
      const amount = calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount);

      paymentRequest.update({
        total: {
          label: currentPlan.sanitizedPlanTitle,
          amount,
        },
      });

      paymentRequest.canMakePayment().then((result) => {
        if (result) {
          // hack!
          // paymentRequest interface does not expose "hasRegisteredListener"
          // but in fact it is accessible...
          if ((paymentRequest as any).hasRegisteredListener('paymentmethod')) {
            paymentRequest.off('paymentmethod');
          }

          paymentRequest.on('paymentmethod', handleFormSubmit);
        }
      });
    }
  }, [paymentRequest, discount]);

  return (
    <BaseStepV2 {...props}>
      <Dialog open={alert.length > 0} onClose={() => setAlert('')}>
        <Alert severity="error">{alert}</Alert>
      </Dialog>

      <MobilePaymentSummary
        planName={currentPlan.sanitizedDescription}
        planPriceInCents={currentPlan.sanitizedAmount}
        planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
        planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
        appliedDiscount={discount}
        planCurrency={currentPlan.sanitizedCurrency}
        showCouponForm={showCouponInput}
        planPriceWithoutBargainShort={currentPlan.sanitizedPriceWithoutBargainShort}
      />

      <PaymentPageTwoSidedLayout>
        <ContentCol location="left">
          <ContentBoxWrapper>
            <StepV2MainContent
              {...props}
              isButtonDisabled={!isCardInfoCorrect}
              onButtonClick={handleFormSubmit}
              isLoading={isLoading}
            >
              {paymentMethod === PaymentMethod.CreditCard && (
                <CardForm setIsCardInfoCorrect={setIsCardInfoCorrect} paymentRequest={paymentRequest} />
              )}
            </StepV2MainContent>
            <PaymentSecurityBoxWrapper>
              <PaymentSecurityBox />
            </PaymentSecurityBoxWrapper>
          </ContentBoxWrapper>
        </ContentCol>
        <PaymentSummaryCol location="right">
          <ContentBoxWrapper offsetVariant={80}>
            <MainContentBox>
              <LogoWithName />
              <PaymentSummary
                planName={currentPlan.sanitizedDescription}
                planPriceInCents={currentPlan.sanitizedAmount}
                planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
                planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
                appliedDiscount={discount}
                planCurrency={currentPlan.sanitizedCurrency}
                showCouponForm={showCouponInput}
              />
            </MainContentBox>
          </ContentBoxWrapper>
        </PaymentSummaryCol>
      </PaymentPageTwoSidedLayout>
    </BaseStepV2>
  );
});
